import { useState, type ReactNode } from 'react';
import { Card, Alert, Tag } from '../../ui';
import { ChevronDown } from 'lucide-react';
import { Lock, Percent, CheckCircle2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { inr2, plural } from '../../lib/format';
import { formatIstDate } from '../../lib/time';
import { CopyValue } from '../../components/CopyValue';
import { PALETTE } from '../../lib/palette';

/**
 * Advance / prepaid cash-discount panel on the Order detail page — PURELY INFORMATIONAL. The CD is applied
 * FULLY AUTOMATICALLY for every eligible order: chosen at placement (ESTIMATED), LOCKED the moment a verified
 * payment covers the net, and dropped at billing if no covering payment ever arrives. There are deliberately NO
 * manual CD actions (no Lock, no Clear, no Apply) — nothing to do; simply not prepaying bills the order at full.
 *  - ESTIMATED → the pending benefit + "applies automatically once the payment is verified".
 *  - LOCKED    → the applied discount summary (who/when + audit remarks).
 * Shows nothing for a NONE order (no discount to display).
 */
/**
 * [pendingAmt] — money already submitted against the order and waiting for Accounts (from the page's pay
 * summary). Without it this card said "NOT YET PAID · Still to pay ₹7,646" beside a header saying the
 * ₹7,646 was paid and awaiting verification.
 */
export function AdvanceCdPanel({ orderId, pendingAmt = 0 }: { orderId: string; pendingAmt?: number }) {
  const role = useAuthStore((s) => s.user?.role);
  // Buyer (retailer/farmer) vs internal staff — drives "you saved" vs "the retailer saved" wording + hides jargon.
  const isCustomer = role === 'RETAILER' || role === 'FARMER';

  const { data: cd } = useQuery({
    queryKey: ['advance-cd', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/advance-cd` }),
    enabled: !!orderId,
  });

  if (!cd) return null;
  const status: 'NONE' | 'ESTIMATED' | 'LOCKED' = cd.status;
  // Nothing to display for a NONE order — the panel is a status view for ESTIMATED/LOCKED only.
  if (status === 'NONE') return null;

  // "You saved" = original grand − net payable. Its parts are the CD on goods + the GST it removes, PLUS
  // the whole-rupee round-off on the net (rounding the net DOWN means you pay a few paise less). Fold the
  // round-off term in so the parenthetical actually sums to the headline figure — no silent 0.10 gap.
  const r2c = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
  const netGstTop = r2c(Number(cd.net?.cgst ?? 0) + Number(cd.net?.sgst ?? 0) + Number(cd.net?.igst ?? 0));
  const roundSaving = r2c((Number(cd.net?.taxable ?? 0) + netGstTop) - Number(cd.netPayable ?? 0));
  const gstText = `${inr2(cd.cdAmount)} taxable + ${inr2(cd.gstReduction)} GST${Math.abs(roundSaving) >= 0.005 ? ` ${roundSaving >= 0 ? '+' : '−'} ${inr2(Math.abs(roundSaving))} round-off` : ''}`;
  const covered = !!cd.paymentCovered; // a verified payment already covers the net → the CD is/gets locked automatically

  /*
   * ONE ANSWER TO "WHAT DOES THE DEALER PAY?".
   *
   * This screen carried three totals — ₹6,114 in the header, ₹5,503 in the discount box, ₹6,100 of
   * payments in the summary — and never said which of them the dealer owes. A reader had to derive it.
   * The strip below states the payable figure, what has actually been received, and what the difference
   * means, from the same numbers the lock and the proforma use.
   */
  const payable = Number(cd.netPayable ?? 0);
  const paidSoFar = Number(cd.approvedPaid ?? 0);
  const diff = Math.round((payable - paidSoFar) * 100) / 100;
  // The part of what is still owed that has ALREADY been submitted and only awaits verification.
  const awaiting = diff > 0.005 ? Math.min(diff, Math.max(0, Number(pendingAmt) || 0)) : 0;
  const awaitingCoversAll = diff > 0.005 && awaiting >= diff - 0.005;
  // What is genuinely still to pay once the submitted-but-unverified money is set aside — the same figure
  // the page header shows ("₹4,646 is still to pay (₹3,000 awaiting verification)").
  const toPay = Math.round((diff - awaiting) * 100) / 100;
  const receipts: any[] = Array.isArray(cd.payments) ? cd.payments : [];
  // Rows come back oldest-first (payment_date, id), so the last one is the most recent receipt.
  const latestReceipt = receipts.length ? receipts[receipts.length - 1] : null;
  const creditPart = Math.round((paidSoFar - receipts.reduce((s, p) => s + (Number(p.amount) || 0), 0)) * 100) / 100;
  /*
   * An ELEMENT, not a nested component. Declared as `const MoneyStrip = () => …` it was a brand-new
   * component type on every render, so React threw the whole strip away and rebuilt it whenever
   * anything re-rendered this panel — which reset the receipts drawer below (toggling dark mode, or a
   * background refetch, slammed it shut mid-read). As an element it reconciles normally and the
   * drawer stays where the reader left it.
   */
  const moneyStrip = (
    <div className="mb-3 overflow-hidden rounded-card border border-line">
      <div className="grid grid-cols-1 gap-px bg-line sm:grid-cols-3">
        <div className="bg-surface p-3">
          <div className="type-overline text-subtle">{isCustomer ? 'You pay (advance)' : 'Retailer pays (advance)'}</div>
          <div className="type-metric tabular-nums text-content">{inr2(payable)}</div>
          {/* NOT "list" — `original.grand` is the order's GRAND TOTAL (goods after any standing
              discount, plus GST), which the Totals card on this same page already calls "Grand total".
              "List" reads as list price/MRP, a number this panel never shows. */}
          <div className="type-caption text-subtle">grand total {inr2(cd.original.grand)} − discount {inr2(cd.totalBenefit)}</div>
        </div>
        <div className="bg-surface p-3">
          <div className="type-overline text-subtle">Received so far</div>
          <div className="type-metric tabular-nums text-content">{inr2(paidSoFar)}</div>
          {/* WHICH receipts make up that total — a figure nobody can trace back to documents is one the
              reader has to go and reconstruct in another tab. The documents themselves are listed under
              the strip: printed here they were a run-on of grey doc numbers that wrapped onto a second
              line and made this tile taller than the two beside it. The caption states only the shape of
              it — how many receipts, and how recent — which is what a reader checks at a glance. */}
          {receipts.length ? (
            <div className="type-caption text-subtle">
              {receipts.length === 1
                ? <>{receipts[0].paymentNo}{receipts[0].date ? ` · ${formatIstDate(receipts[0].date)}` : ''}</>
                : <>{plural(receipts.length, 'receipt')}{latestReceipt?.date ? ` · latest ${formatIstDate(latestReceipt.date)}` : ''}</>}
              {/* Credit taken off the account balance is part of the figure but not a receipt — naming only the receipt
                  put "PAY/…419" (₹20,619) under ₹23,119 (E2E 15 Sep). */}
              {creditPart > 0.005 && <> + {inr2(creditPart)} account credit</>}
            </div>
          ) : (
            /* An order settled entirely off the account balance has money received but no receipt to
               name, and this caption used to call that "no verified payment yet" directly under a
               non-zero figure. Say where it came from instead. */
            <div className="type-caption text-subtle">
              {paidSoFar > 0.005 ? 'from account credit' : 'no verified payment yet'}
            </div>
          )}
        </div>
        <div className="bg-surface p-3">
          <div className="type-overline text-subtle">
            {awaitingCoversAll ? 'Awaiting verification' : diff > 0.005 ? 'Still to pay' : diff < -0.005 ? 'Paid over' : 'Balance'}
          </div>
          <div className={`type-metric tabular-nums ${
            diff > 0.005 ? 'text-warning-600 dark:text-harvest-300'
              : diff < -0.005 ? 'text-brand-700 dark:text-brand-400' : 'text-subtle'}`}>
            {awaitingCoversAll ? inr2(awaiting) : diff > 0.005 ? inr2(toPay) : diff === 0 ? '₹0' : inr2(Math.abs(diff))}
          </div>
          {/*
            * Honest about WHEN this becomes real money on the account.
            *
            * The retailer ledger carries the order at its LIST value until the discount locks — so while
            * the CD is only ESTIMATED, this order still reads as ₹14 owed on the ledger even though the
            * dealer has paid more than the discounted price. Calling it "account credit" already would
            * make two screens disagree. It becomes credit when the lock restates the order (₹6,114 →
            * ₹5,503) and posts the ₹611 back to the ledger.
            */}
          <div className="type-caption text-subtle">
            {awaitingCoversAll
              ? 'submitted — the discount applies once it is verified'
              : diff > 0.005
              ? (awaiting > 0.005 ? `plus ${inr2(awaiting)} awaiting verification` : 'to earn the discount, before billing')
              : diff < -0.005
                ? (status === 'LOCKED'
                  ? 'over this order — already netted into the account balance'
                  // Receivables are held per ACCOUNT, not per order: an excess here is not money waiting
                  // in a corner, it reduces whatever the dealer owes overall (and is only refundable if
                  // the whole account goes into credit).
                  : 'over this order — nets against the account balance when the discount is applied at billing')
                : 'nothing outstanding'}
          </div>
        </div>
      </div>
      <Receipts payments={receipts} total={paidSoFar} />
    </div>
  );

  // ── LOCKED ────────────────────────────────────────────────────────────────
  if (status === 'LOCKED') {
    return (
      <Card style={{ marginBottom: 12, borderColor: '#b7eb8f' }}
        title={<span><Lock size={16} style={{ color: 'var(--color-success-600)' }} /> Advance cash discount — locked</span>}>
        {moneyStrip}
        <Alert type="success" style={{ marginBottom: 10 }}
          title={`${cd.pct}% cash discount applied${cd.appliedLabel ? ` (${cd.appliedLabel})` : ''} — ${isCustomer ? 'you saved' : 'the retailer saved'} ${inr2(cd.totalBenefit)} (${gstText}).`} />
        <Derivation cd={cd} isCustomer={isCustomer} />
        {!isCustomer && (
          <div className="mt-2 text-xs text-muted">
            Locked automatically{cd.lockedByName && cd.lockedByName !== 'System' ? ` (recorded ${cd.lockedByName})` : ' on payment verification'}{cd.lockedAt ? ` · ${new Date(cd.lockedAt).toLocaleString('en-IN')}` : ''}.
            {cd.remarks ? <> Remarks: <i>{cd.remarks}</i></> : null}
          </div>
        )}
      </Card>
    );
  }

  // ── ESTIMATED ───────────────────────────────────────────────────────────────
  if (status === 'ESTIMATED') {
    return (
      <Card style={{ marginBottom: 12, borderColor: '#ffd591' }}
        // A header saying "pending verification" above a green line saying the payment already covers
        // the net is the contradiction this page was criticised for. One state, one sentence.
        // Just the subject: the card title is truncated by the kit, and on a phone the state clause was
        // cut mid-word — while the tag beside it already says the state in one word.
        title={<span><Percent size={16} style={{ color: covered ? 'var(--color-success-600)' : 'var(--color-warning-600)' }} /> Advance cash discount</span>}
        extra={<Tag tone={covered ? 'green' : 'gold'}>{covered ? 'EARNED' : awaitingCoversAll ? 'PAID · AWAITING VERIFICATION' : awaiting > 0.005 ? 'PART PAID' : 'NOT YET PAID'}</Tag>}>
        {moneyStrip}
        {/* The alert used to say "pending payment verification" in every case — including when the
            payment had already been verified, directly under a heading saying so. It now reports the
            state the strip above shows. */}
        <Alert type={covered ? 'success' : 'warning'} style={{ marginBottom: 10 }}
          title={covered
            ? `Discount earned — ${isCustomer ? 'you save' : 'the retailer saves'} ${inr2(cd.totalBenefit)} (${gstText}).`
            : awaitingCoversAll
              ? `Payment awaiting verification — once verified ${isCustomer ? 'you save' : 'the retailer saves'} ${inr2(cd.totalBenefit)} (${gstText}).`
            : (!isCustomer
              ? `Advance CD benefit${cd.appliedLabel ? ` (${cd.appliedLabel})` : ''} not yet earned — ${inr2(cd.totalBenefit)} (${gstText}).`
              : `Advance cash discount${cd.appliedLabel ? ` (${cd.appliedLabel})` : ''} not yet claimed — you save ${inr2(cd.totalBenefit)} (${gstText}).`)}
          description={covered
            ? <span>
                Verified payments of <b>{inr2(cd.approvedPaid)}</b> cover the net payable of <b>{inr2(cd.netPayable)}</b>, so the
                order will be billed at the discounted value. Until it is billed the ledger still carries this order
                at <b>{inr2(cd.original.grand)}</b> — the {inr2(cd.totalBenefit)} discount is credited to the account when the
                discount is applied, and only then does any overpayment show as credit.
              </span>
            : awaitingCoversAll
              ? <span>{inr2(awaiting)} {isCustomer ? 'has been paid' : 'has been submitted'} against the net payable of <b>{inr2(cd.netPayable)}</b> (was {inr2(cd.original.grand)}). Once it is verified the order bills at the discounted value; if it is not verified by billing, the order bills at {inr2(cd.original.grand)}.</span>
            : awaiting > 0.005
              ? <span>Net payable <b>{inr2(cd.netPayable)}</b> (was {inr2(cd.original.grand)}). {inr2(awaiting)} is awaiting verification — {isCustomer ? 'pay' : 'record'} the remaining <b>{inr2(toPay)}</b> before billing to {isCustomer ? 'claim' : 'earn'} the discount; if the full net is not verified by billing, the order bills at {inr2(cd.original.grand)}.</span>
            : (!isCustomer
              ? <span>Net payable <b>{inr2(cd.netPayable)}</b> (was {inr2(cd.original.grand)}). It applies <b>automatically</b> once a verified payment covers the net; if none is received by billing, the order bills at {inr2(cd.original.grand)}.</span>
              : <span>Net payable <b>{inr2(cd.netPayable)}</b> (was {inr2(cd.original.grand)}). Pay the net before billing to claim this discount — it applies automatically once your payment is verified.</span>)} />
        <Derivation cd={cd} isCustomer={isCustomer} />

        {cd.frozen ? (
          <Alert style={{ marginTop: 10 }} type="info" title="A Tally invoice already exists — the advance CD is frozen and can no longer be changed." />
        ) : (
          // No manual CD actions: an eligible order's discount is applied AUTOMATICALLY the moment a verified
          // payment covers the net. This line just reports where that stands (not prepaying simply bills full).
          <div style={{ marginTop: 10, fontSize: 13 }}>
            {/* Only the not-yet-covered, nothing-pending case says anything here: when it IS covered the alert above
                already says so (and, with money awaiting verification, what is left to pay), and printing it twice made the panel look like two verdicts. */}
            {covered || awaiting > 0.005
              ? null
              : <span style={{ color: 'var(--color-warning-600)' }}>{isCustomer ? 'Pay' : 'Record & verify'} the net {inr2(cd.netPayable)} before billing — the discount applies automatically once the payment is verified{isCustomer ? '' : ` (verified so far: ${inr2(cd.approvedPaid)})`}. If none is received by billing, the order bills at {inr2(cd.original.grand)}.</span>}
          </div>
        )}
      </Card>
    );
  }

  // NONE is filtered out above (nothing to display); ESTIMATED + LOCKED are the only rendered states.
  return null;
}

/**
 * The receipts behind "Received so far" — the strip's own footer, folded away by default.
 *
 * Printed inline under the metric these were a run-on of grey doc numbers ("PAY/2026/000363 ₹1,500 ·
 * PAY/2026/000362 ₹3,500 · …") that wrapped onto a second line, made that tile taller than the two
 * beside it, and could not be read as a column of amounts. Same facts, one click away and aligned:
 * date, document, mode, amount — with a total that visibly adds up to the figure above.
 *
 * MODULE SCOPE, deliberately: it holds open/closed state, and a component defined inside a render is
 * a new component type on every render — React would remount it and the drawer would slam shut
 * whenever anything else on the page changed. Same reason the strip above it is an element, not a
 * nested component.
 */
function Receipts({ payments, total }: { payments: any[]; total: number }) {
  const [open, setOpen] = useState(false);
  if (!payments.length) return null;
  const r2c = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
  /*
   * Money taken off this order from the account balance counts towards "received so far" but has no
   * receipt to name (the API lists cash/bank documents only). Naming the remainder keeps the rows
   * adding up to the headline figure — a total whose parts come up short is the thing this list
   * exists to prevent.
   */
  const credit = r2c(total - payments.reduce((s, p) => s + (Number(p.amount) || 0), 0));
  return (
    <div className="border-t border-line bg-surface-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1 px-3 py-1.5 type-caption text-brand-700 hover:underline dark:text-brand-400">
        <ChevronDown size={13} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
        {open ? 'Hide the receipts' : `Show the ${plural(payments.length, 'receipt')}`}
      </button>
      {open && (
        <div className="border-t border-line bg-surface">
          {payments.map((p) => (
            <ReceiptRow key={p.id} date={p.date} doc={p.paymentNo} mode={p.mode} amount={p.amount} />
          ))}
          {credit > 0.005 && <ReceiptRow doc="Account credit applied" amount={credit} noDoc />}
          <div className="flex items-center gap-2 border-t border-line bg-surface-2 px-3 py-1.5">
            <span className="type-label-strong text-content">Total received</span>
            <span className="ml-auto type-label-strong tabular-nums text-content">{inr2(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** One receipt line: when it happened · which document (click to copy) · how it came in · how much. */
function ReceiptRow({ date, doc, mode, amount, noDoc }: {
  date?: string | null; doc: string; mode?: string | null; amount: any;
  /** A derived line (account credit) — a label, not a document, so no copy affordance. */
  noDoc?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 border-t border-surface-2 px-3 py-1.5 first:border-t-0">
      <span className="w-21.5 shrink-0 type-label tabular-nums text-subtle">{date ? formatIstDate(date) : ''}</span>
      {noDoc
        ? <span className="truncate type-label text-muted">{doc}</span>
        : <CopyValue value={doc} label="Payment no"><span className="type-label-strong text-content">{doc}</span></CopyValue>}
      {/* The mode is context, not the answer — first thing to go when the row runs out of width.
          Hidden on a WRAPPER, not via a class on the Tag: `cn()` carries no tailwind-merge, so a
          `hidden` handed to Tag loses to the `inline-flex` in its own base classes and the chip
          stayed on screen at 390px, pushing the amount out of the row. */}
      {mode ? <span className="hidden shrink-0 sm:inline-flex"><Tag>{mode}</Tag></span> : null}
      <span className="ml-auto shrink-0 type-label tabular-nums text-content">{inr2(amount)}</span>
    </div>
  );
}

/**
 * The derivation, folded away by default.
 *
 * Ten rows of arithmetic are the answer to "prove it", not to "what do I owe" — and the three-figure
 * strip above already answers the second. Keeping it expanded pushed the rest of the order off the
 * screen on every visit; it is one click away and nothing has been removed.
 */
function Derivation({ cd, isCustomer }: { cd: any; isCustomer?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 type-caption text-brand-700 hover:underline dark:text-brand-400">
        <ChevronDown size={13} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
        {open ? 'Hide the calculation' : 'Show how this is calculated'}
      </button>
      {open && <div className="mt-2"><Breakdown cd={cd} isCustomer={isCustomer} /></div>}
    </div>
  );
}

// Full amount trail in two clearly-labelled steps so anyone can follow it: Step 1 builds the original
// grand total (list → standing discount → taxable → GST); Step 2 applies the advance cash discount — and
// spells out that the % is taken on the TAXABLE value (not the grand total) → net payable → saving.
function Breakdown({ cd, isCustomer }: { cd: any; isCustomer?: boolean }) {
  const r2 = (n: number) => Math.round((n || 0) * 100) / 100;
  /*
   * WHICH TAX, not just how much. "GST + ₹514" leaves the reader to work out from another card whether
   * this is one head or two — and the split is what they have to key into Tally.
   */
  const gstHeads = (t: any) => {
    const igst = r2(Number(t?.igst ?? 0));
    if (igst > 0) return <span className="text-subtle"> · IGST {inr2(igst)}</span>;
    const c = r2(Number(t?.cgst ?? 0)), sg = r2(Number(t?.sgst ?? 0));
    if (c <= 0 && sg <= 0) return null;
    return <span className="text-subtle"> · CGST {inr2(c)} + SGST {inr2(sg)}</span>;
  };
  const origGst = r2((cd.original?.cgst ?? 0) + (cd.original?.sgst ?? 0) + (cd.original?.igst ?? 0));
  const netGst = r2((cd.net?.cgst ?? 0) + (cd.net?.sgst ?? 0) + (cd.net?.igst ?? 0));
  const netRoundOff = r2(Number(cd.netPayable) - (Number(cd.net?.taxable ?? 0) + netGst));
  const hasStandingDisc = cd.originalSubtotal != null && Number(cd.originalDiscount) > 0;
  const stdPct = hasStandingDisc && Number(cd.originalSubtotal) > 0 ? r2((Number(cd.originalDiscount) / Number(cd.originalSubtotal)) * 100) : 0;
  const taxable0 = inr2(cd.original.taxable);
  const cd0 = inr2(cd.cdAmount);
  return (
    <div style={{ border: '1px solid var(--gb-border)', borderRadius: 8, overflow: 'hidden', fontSize: 13 }}>
      {/* ── Step 1: build the original grand total ── */}
      <Section>Step 1 · Order value (before cash discount)</Section>
      {hasStandingDisc && (
        <>
          <Row label="List value (before discount)" value={inr2(cd.originalSubtotal)} />
          <Row label={`Less: standing discount (${stdPct}%)`} value={`− ${inr2(cd.originalDiscount)}`} accent={PALETTE.danger[600]} />
        </>
      )}
      <Row label="Taxable value (goods, before GST)" value={inr2(cd.original.taxable)} />
      <Row label={<>GST{gstHeads(cd.original)}</>} value={`+ ${inr2(origGst)}`} />
      <Row label="Original grand total" value={<b>{inr2(cd.original.grand)}</b>} strong />

      {/* ── Step 2: apply the advance cash discount ON THE TAXABLE value ── */}
      <Section>Step 2 · Advance cash discount — {Number(cd.pct)}% of the taxable value ({taxable0}), <u>not</u> the grand total</Section>
      <Row label={`Cash discount = ${Number(cd.pct)}% × ${taxable0} (taxable)`} value={`− ${inr2(cd.cdAmount)}`} accent={PALETTE.danger[600]} />
      <Row label={`Net taxable value = ${taxable0} − ${cd0}`} value={inr2(cd.net.taxable)} />
      <Row label={<>GST on net{gstHeads(cd.net)}</>} value={`+ ${inr2(netGst)}`} />
      {Math.abs(netRoundOff) >= 0.005 && (
        <Row label="Round-off" value={`${netRoundOff < 0 ? '−' : '+'} ${inr2(Math.abs(netRoundOff))}`} />
      )}
      <Row label="Net payable" value={<b>{inr2(cd.netPayable)}</b>} strong />
      <Row label={isCustomer ? 'You save' : 'Retailer saves'} value={<b style={{ color: 'var(--color-success-600)' }}>{inr2(cd.totalBenefit)}</b>} strong />
    </div>
  );
}

// A muted section-label row that groups the two calculation steps.
function Section({ children }: { children: any }) {
  return (
    <div className="border-t border-line bg-surface-2 px-3 py-1.5 text-[11.5px] font-semibold text-muted">
      {children}
    </div>
  );
}

function Row({ label, value, accent, strong }: { label: ReactNode; value: any; accent?: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid var(--gb-surface-2)', background: strong ? '#fafafa' : undefined }}>
      <span style={{ color: '#595959' }}>{label}</span>
      <span style={{ color: accent, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}
